import { BrowserModule } from '@angular/platform-browser';
import { ErrorHandler, NgModule } from '@angular/core';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgxsModule, NgxsModuleOptions, Store } from '@ngxs/store';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { ToastrModule } from 'ngx-toastr';
import { ServiceWorkerModule } from '@angular/service-worker';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { AgentAssistantModule } from '@nimbus-library/features/agent-assistant/agent-assistant.module';
import { GlobalErrorHandler } from '@nimbus-global/features/shared';
import { MenuAuthService } from '@nimbus-global/features/menu/services/menu-auth.service';
import { IGeoLocationService } from '@nimbus-global/features/geo-location/services/i-geo-location.service';
import { GeoLocationModule } from '@nimbus-global/features/geo-location/geo-location.module';
import { DefaultProfilePictureService } from '@nimbus-library/features/user-settings/services/defaults/default-profile-picture.service';
import { DefaultRunbookService } from '@nimbus-library/features/user-settings/services/defaults/default-runbook.service';
import { DefaultUserSettingsStatusService } from '@nimbus-library/features/user-settings/services/defaults/default-user-settings-status.service';
import { UserSettingsModule } from '@nimbus-library/features/user-settings/user-settings.module';
import { LocalizationModule } from '@nimbus-library/features/localization/localization.module';
import { NimbusAdminCommonModule } from '@nimbus-admin/common/nimbus-admin-common.module';
import { MenuModule } from '@nimbus-admin/features/menu/menu.module';
import { NavigationModule } from '@nimbus-library/features/navigation/navigation.module';
import { ErrorCodeProviderService } from '@nimbus-global/features/shared/services/error-code-provider.service';
import { BaseErrorCodeProviderService } from '@nimbus-library/features/shared/services/base-error-code-provider.service';
import { IPermissionService } from '@nimbus-library/features/feature-permission/services/contracts/i-permission.service';
import { PermissionService } from '@nimbus-library/features/feature-permission/services/permission.service';
import { AppConfiguration } from '@nimbus-admin/configuration/app.config';
import { APP_CONFIG } from '@nimbus-global/features/shared/i-app.configuration';
import { OrganizationUnitModule } from '@nimbus-admin/features/organization-unit/organization-unit.module';
import { ThemeResolver } from '@nimbus-library/features/shared/store/theme-store/theme-resolver';
import { SharedUIModule } from '@nimbus-library/features/shared/shared.ui.module';
import { NimbusCacheBrowserModule } from '@nimbus-library/features/cache/nimbus-cache.browser.module';
import { ModalModule } from '@nimbus-library/features/modal/modal.module';
import { NimbusChartsModule } from '@nimbus-library/features/charts/nimbus-charts.module';
import { MenuState } from '@nimbus-library/features/menu';
import { NimbusDateAdapter, NimbusDateFormats } from '@nimbus-library/features/localization/services/nimbus-date-adapter';
import { IOrganizationUnitDataService } from '@nimbus-global/features/shared/services/api/organization-unit/organization-unit-data.service';
import { OrganizationUnitDataService } from '@nimbus-admin/common/services/organization-unit/organization-unit-data.service';
import { BaseTeamsService } from '@nimbus-library/features/shared/services/team/base/base-teams.service';
import { TeamsService } from '@nimbus-library/features/shared/services/team/teams.service';
import { TrackingModule } from '@nimbus-library/features/tracking/tracking.module';
import { TrackingConfigurationService } from '@nimbus-admin/common/services/tracking-configuration.service';
import { TeamsApplicationConfigurationProviderFake } from '@nimbus-admin/common/services/teams-application-configuration.provider.fake.';
import { IPermissionManagerService } from '@nimbus-library/features/user-settings/services/contracts/i-permission-manager.service';
import { AdminPermissionManagerService } from '@nimbus-global/features/permissions/services/domain/admin-permission-manager.service';
import { TeamsContextModule } from '@nimbus-library/features/microsoft-teams/teams-context.module';
import { ITeamsApplicationConfigurationProvider } from '@nimbus-library/features/microsoft-teams/models/i-teams-application-configuration.provider';
import { LoggingModule } from '@nimbus-library/features/logger/logging.module';
import { ILogger } from '@nimbus-library/features/logger/services/logger/i-logger';
import { ServiceWorkerBypassInterceptor } from '@nimbus-library/features/shared/interceptors/service-worker-bypass.interceptor';
import { PageVisibilityBrowserModule } from '@nimbus-library/features/page-visibility/browser/page-visibility.browser.module';
import { ConnectionAvailabilityBrowserModule } from '@nimbus-library/features/connection-availability/browser/connection-availability.browser.module';
import { IEnvironmentService } from '@nimbus-library/features/configuration/i-environment.service';
import { BulkProcessService } from '@nimbus-admin/infrastructure/bulk-process/bulk-process.service';
import { ChatModule } from '@nimbus-global/features/chat/chat.module';
import { ApiQueryParamsInterceptor } from '@nimbus-library/features/shared/interceptors/api-query-params.interceptor';
import { CompanionChatPanelComponent } from '@nimbus-library/features/companion-chat/components/companion-chat-panel/companion-chat-panel.component';
import { CompanionChatResolver } from '@nimbus-library/features/companion-chat/services/companion-chat-resolver';
import { CompanionChatResolver as AdminCompanionChatResolver } from '@nimbus-admin/features/companion-chat/services/companion-chat-resolver.service';
import { CompanionSplitScreenResizerComponent } from '@nimbus-library/features/companion-chat/components/companion-split-screen-resizer/companion-split-screen-resizer.component';
import { environment } from '../environments/environment';
import { EnvironmentAdminService } from '../environments/environment-admin.service';
import { GeoLocationService } from './features/geo-location/geo-location.service';
import { AdminLocalizationService } from './features/localization/admin-localization.service';
import { FeaturePermissionModule } from './features/feature-permission/feature-permission.module';
import { AdminAuthneticationModule } from './features/authentication/admin-authentication.module';
import { SharedModule } from './features/shared/shared.module';
import { AdminUiModule } from './features/shared/admin-ui.module';
import { ConfigurationModule } from './configuration/configuration.module';
import { AppComponent } from './app.component';
import { RoutingModule } from './routing/routing.module';

export const ngxsConfig: NgxsModuleOptions = {
  selectorOptions: {
    suppressErrors: false,
    injectContainerState: false,
  },
};

@NgModule({
  declarations: [AppComponent],
  imports: [
    NgxsModule.forRoot([MenuState], ngxsConfig),
    NgxsReduxDevtoolsPluginModule.forRoot({ disabled: true }),
    ToastrModule.forRoot(),
    AdminAuthneticationModule,
    LoggingModule.forRoot(
      {
        loggingConfiguration: {
          cloudLoggingEnabled: environment.production,
          consoleLoggingEnabled: true,
          zipkinLoggingEnabled: false,
        },
      },
      'Nimbus.UI.Admin',
    ),
    NimbusCacheBrowserModule.forRoot(ILogger),
    BrowserModule,
    BrowserAnimationsModule,
    PageVisibilityBrowserModule,
    ConnectionAvailabilityBrowserModule,
    SharedModule,
    NavigationModule,
    MenuModule,
    FeaturePermissionModule,
    RoutingModule,
    UserSettingsModule.forRoot(
      {
        disableLanguageSelector: false,
        hideFeatureList: true,
        hideLicenseList: true,
        hideRunbookInformation: true,
      },
      DefaultUserSettingsStatusService,
      DefaultProfilePictureService,
      DefaultRunbookService,
    ),
    ConfigurationModule,
    AdminUiModule,
    NimbusChartsModule,
    ModalModule.forRoot(ThemeResolver),
    MatNativeDateModule,
    SharedUIModule,
    OrganizationUnitModule,
    AgentAssistantModule,
    LocalizationModule.forRoot(AdminLocalizationService, {
      fileSuffix: '.json',
      folderPrefix: 'i18n/',
    }),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerImmediately',
    }),
    NimbusAdminCommonModule,
    GeoLocationModule,
    ChatModule,
    TeamsContextModule.forRoot({ contextItemName: 'Nimbus.UserContext' }, TeamsApplicationConfigurationProviderFake),
    TrackingModule.forRoot(TrackingConfigurationService),
    CompanionChatPanelComponent,
    CompanionSplitScreenResizerComponent,
  ],
  providers: [
    { provide: CompanionChatResolver, useClass: AdminCompanionChatResolver },
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    { provide: APP_CONFIG, useValue: AppConfiguration },
    { provide: BaseErrorCodeProviderService, useClass: ErrorCodeProviderService },
    { provide: IPermissionService, useClass: PermissionService },
    { provide: IEnvironmentService, useClass: EnvironmentAdminService },
    { provide: IGeoLocationService, useClass: GeoLocationService },
    { provide: IPermissionManagerService, useClass: AdminPermissionManagerService },
    MenuAuthService,
    { provide: DateAdapter, useClass: NimbusDateAdapter, deps: [MAT_DATE_LOCALE, Store] },
    { provide: MAT_DATE_FORMATS, useValue: NimbusDateFormats },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: IOrganizationUnitDataService, useClass: OrganizationUnitDataService },
    { provide: BaseTeamsService, useClass: TeamsService },
    { provide: ITeamsApplicationConfigurationProvider, useClass: TeamsApplicationConfigurationProviderFake },
    { provide: HTTP_INTERCEPTORS, useClass: ServiceWorkerBypassInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ApiQueryParamsInterceptor, multi: true },
    BulkProcessService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
